import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet, ArrowDownCircle, ArrowUpCircle, TrendingUp, Plus,
  CheckCircle, XCircle, Clock, Upload, Copy, Bitcoin, DollarSign,
  Tag, RefreshCw, Trash2, ShoppingCart, Send, Star, MessageCircle
} from "lucide-react";

// Admin payment info (HTG via MonCash/NatCash)
const ADMIN_HTG_NUMBERS = ["+50947096761", "+50946261180", "+50955460588"];
const ADMIN_WHATSAPP = "50946261180";
// 2026-06-09 : client-facing WA CTAs go to the service group instead of the admin's DM.
const SERVICE_GROUP_URL = "https://chat.whatsapp.com/ErJCTqoXDZ24WxqGd9LTOv?s=cl&p=a&mlu=0&amv=2";
const ADMIN_WA_MSG_CRYPTO = encodeURIComponent("Bonjour, je souhaite effectuer un échange crypto sur SMARTECH PRO.");
// eslint-disable-next-line no-unused-vars
const ADMIN_WA_URL_CRYPTO = SERVICE_GROUP_URL;

const fmt = (n, digits = 2) => {
  const num = Number(n || 0);
  return num.toLocaleString("fr-FR", { maximumFractionDigits: digits, minimumFractionDigits: digits });
};

const fmtCrypto = (n) => {
  const num = Number(n || 0);
  if (num === 0) return "0";
  return num.toLocaleString("fr-FR", { maximumFractionDigits: 8 });
};

const short = (s, n = 8) => {
  if (!s) return "";
  if (s.length <= n * 2 + 3) return s;
  return `${s.slice(0, n)}...${s.slice(-n)}`;
};

const statusColor = (st) => {
  if (st === "pending" || st === "pending_admin") return "#F59E0B";
  if (st === "approved" || st === "completed") return "#10B981";
  if (st === "rejected" || st === "cancelled") return "#EF4444";
  return "#64748B";
};

const statusLabel = (st) => ({
  pending: "En attente",
  pending_admin: "En attente admin",
  approved: "Approuvé",
  completed: "Complété",
  rejected: "Rejeté",
  cancelled: "Annulé",
  active: "Actif",
}[st] || st);

// =========================================================
// Component: User-facing Crypto Exchange
// =========================================================
export const CryptoExchangeUser = ({ apiCall, API, tokenKey = "client_token", role = "client" }) => {
  const [tab, setTab] = useState("wallet"); // wallet | market | mine | history
  const [config, setConfig] = useState({ supported_symbols: [], fee_rate_per_side: 0.005, admin_whatsapp: "" });
  const [wallet, setWallet] = useState(null);
  const [ads, setAds] = useState([]);
  const [myAds, setMyAds] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);

  // Forms
  const [depCurrency, setDepCurrency] = useState("HTG");
  const [depAmount, setDepAmount] = useState("");
  const [depTxHash, setDepTxHash] = useState("");
  const [depProofUrl, setDepProofUrl] = useState("");
  const [depUploading, setDepUploading] = useState(false);

  const [wdrCurrency, setWdrCurrency] = useState("HTG");
  const [wdrAmount, setWdrAmount] = useState("");
  const [wdrDestination, setWdrDestination] = useState("");

  const [newAdSymbol, setNewAdSymbol] = useState("USDT-TRC20");
  const [newAdAmount, setNewAdAmount] = useState("");
  const [newAdPrice, setNewAdPrice] = useState("");

  const [buyAd, setBuyAd] = useState(null);
  const [buyAmount, setBuyAmount] = useState("");
  const [buyAddress, setBuyAddress] = useState("");

  const [adminAddresses, setAdminAddresses] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, w, ad, ma, d, wd, tr, addrs] = await Promise.all([
        apiCall("get", "/crypto/config"),
        apiCall("get", "/crypto/wallet"),
        apiCall("get", "/crypto/ads"),
        apiCall("get", "/crypto/ads/mine"),
        apiCall("get", "/crypto/deposits"),
        apiCall("get", "/crypto/withdrawals"),
        apiCall("get", "/crypto/trades"),
        apiCall("get", "/crypto/addresses"),
      ]);
      setConfig(c.data);
      setWallet(w.data);
      setAds(ad.data.ads || []);
      setMyAds(ma.data.ads || []);
      setDeposits(d.data.deposits || []);
      setWithdrawals(wd.data.withdrawals || []);
      setTrades(tr.data.trades || []);
      setAdminAddresses(addrs.data.addresses || []);
    } catch (e) {
      toast.error("Erreur chargement crypto");
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const uploadProof = async (file) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/crypto/upload-proof`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(tokenKey)}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return res.data.url;
    } catch (e) {
      toast.error("Erreur téléversement");
      return null;
    }
  };

  const handleDepositProofUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setDepUploading(true);
    const url = await uploadProof(f);
    if (url) {
      setDepProofUrl(url);
      toast.success("Preuve téléversée");
    }
    setDepUploading(false);
  };

  const submitDeposit = async () => {
    if (!depCurrency || !depAmount || Number(depAmount) <= 0) {
      toast.error("Montant invalide"); return;
    }
    if (!depProofUrl) { toast.error("Preuve requise"); return; }
    try {
      await apiCall("post", "/crypto/deposit", {
        currency: depCurrency,
        amount: Number(depAmount),
        proof_url: depProofUrl,
        tx_hash: depTxHash,
      });
      toast.success("Dépôt soumis – en attente de validation admin");
      setDepAmount(""); setDepTxHash(""); setDepProofUrl("");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const submitWithdraw = async () => {
    if (!wdrCurrency || !wdrAmount || Number(wdrAmount) <= 0) {
      toast.error("Montant invalide"); return;
    }
    if (!wdrDestination.trim()) { toast.error("Destination requise"); return; }
    try {
      await apiCall("post", "/crypto/withdraw", {
        currency: wdrCurrency,
        amount: Number(wdrAmount),
        destination: wdrDestination.trim(),
      });
      toast.success("Demande de retrait soumise");
      setWdrAmount(""); setWdrDestination("");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const submitNewAd = async () => {
    if (!newAdAmount || Number(newAdAmount) <= 0 || !newAdPrice || Number(newAdPrice) <= 0) {
      toast.error("Valeurs invalides"); return;
    }
    try {
      await apiCall("post", "/crypto/ads", {
        crypto_symbol: newAdSymbol,
        amount_crypto: Number(newAdAmount),
        price_per_unit_htg: Number(newAdPrice),
      });
      toast.success("Annonce créée – votre solde crypto est verrouillé");
      setNewAdAmount(""); setNewAdPrice("");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const cancelMyAd = async (adId) => {
    if (!window.confirm("Annuler cette annonce et débloquer votre solde?")) return;
    try {
      await apiCall("delete", `/crypto/ads/${adId}`);
      toast.success("Annonce annulée");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const submitBuy = async () => {
    if (!buyAd || !buyAmount || Number(buyAmount) <= 0) {
      toast.error("Montant invalide"); return;
    }
    try {
      await apiCall("post", "/crypto/trades", {
        ad_id: buyAd.id,
        amount_crypto: Number(buyAmount),
        buyer_crypto_address: buyAddress.trim(),
      });
      toast.success("Trade créé – en attente de validation admin");
      setBuyAd(null); setBuyAmount(""); setBuyAddress("");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const cryptoBalances = wallet?.crypto_available || {};
  const cryptoLocked = wallet?.crypto_locked || {};
  const allSymbols = Array.from(new Set([
    ...Object.keys(cryptoBalances),
    ...Object.keys(cryptoLocked),
  ]));

  return (
    <div data-testid="crypto-exchange-user" style={{ fontSize: "0.85rem" }}>
      {/* Header */}
      <div style={{
        padding: "0.8rem 1rem",
        background: "linear-gradient(135deg, #0F172A, #1E293B)",
        borderRadius: "12px",
        color: "white",
        marginBottom: "1rem",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem"
      }}>
        <div>
          <div style={{ fontSize: "1rem", fontWeight: 800, display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <Bitcoin size={18} color="#F59E0B" /> Échange Crypto
          </div>
          <div style={{ fontSize: "0.7rem", opacity: 0.8, marginTop: "0.2rem" }}>
            P2P avec escrow admin · Frais {(config.fee_rate_per_side * 100).toFixed(1)}% par partie
          </div>
        </div>
        <button onClick={loadAll} className="btn btn-sm" style={{ background: "rgba(255,255,255,0.1)", color: "white", border: "1px solid rgba(255,255,255,0.2)" }} data-testid="crypto-refresh-btn">
          <RefreshCw size={14} /> Actualiser
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "0.3rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        {[
          { k: "wallet", label: "Portefeuille", icon: Wallet },
          { k: "market", label: "Marché", icon: TrendingUp },
          { k: "mine", label: "Mes Annonces", icon: Tag },
          { k: "history", label: "Historique", icon: Clock },
        ].map(t => {
          const Ic = t.icon;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`crypto-tab-${t.k}`}
              style={{
                padding: "0.45rem 0.7rem",
                borderRadius: "8px",
                border: "1px solid " + (tab === t.k ? "#2563EB" : "#E2E8F0"),
                background: tab === t.k ? "#2563EB" : "white",
                color: tab === t.k ? "white" : "#1E293B",
                fontSize: "0.78rem",
                fontWeight: 700,
                display: "flex", alignItems: "center", gap: "0.3rem",
                cursor: "pointer"
              }}
            >
              <Ic size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* WALLET TAB */}
      {tab === "wallet" && (
        <div>
          {/* HTG Balance */}
          <div style={{
            padding: "0.8rem", borderRadius: "10px", background: "#EFF6FF",
            border: "1px solid #BFDBFE", marginBottom: "0.7rem"
          }} data-testid="crypto-wallet-htg">
            <div style={{ fontSize: "0.72rem", color: "#1E40AF", fontWeight: 700 }}>
              <DollarSign size={12} style={{ verticalAlign: "middle" }} /> PORTEFEUILLE HTG
            </div>
            <div style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0F172A", marginTop: "0.2rem" }}>
              {fmt(wallet?.htg_available, 2)} <span style={{ fontSize: "0.75rem", color: "#64748B" }}>HTG</span>
            </div>
            {Number(wallet?.htg_locked || 0) > 0 && (
              <div style={{ fontSize: "0.7rem", color: "#F59E0B", marginTop: "0.1rem" }}>
                Verrouillé: {fmt(wallet?.htg_locked, 2)} HTG
              </div>
            )}
          </div>

          {/* Crypto Balances */}
          <div style={{ marginBottom: "0.7rem" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#475569", marginBottom: "0.4rem" }}>CRYPTOS</div>
            {allSymbols.length === 0 ? (
              <div style={{ padding: "0.6rem", background: "#F8FAFC", borderRadius: "8px", fontSize: "0.75rem", color: "#64748B", textAlign: "center" }}>
                Aucun solde crypto. Déposez ci-dessous pour commencer.
              </div>
            ) : (
              allSymbols.map(sym => (
                <div key={sym} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "0.5rem 0.7rem", background: "#F8FAFC", borderRadius: "8px", marginBottom: "0.3rem",
                  border: "1px solid #E2E8F0"
                }} data-testid={`crypto-balance-${sym}`}>
                  <div style={{ fontWeight: 700, color: "#0F172A" }}>{sym}</div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 800 }}>{fmtCrypto(cryptoBalances[sym])}</div>
                    {Number(cryptoLocked[sym] || 0) > 0 && (
                      <div style={{ fontSize: "0.65rem", color: "#F59E0B" }}>Verrouillé: {fmtCrypto(cryptoLocked[sym])}</div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Deposit */}
          <div className="card" style={{ marginBottom: "0.7rem" }}>
            <div style={{ fontWeight: 800, fontSize: "0.88rem", marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <ArrowDownCircle size={15} color="#10B981" /> Déposer des fonds
            </div>
            <div style={{ fontSize: "0.7rem", color: "#64748B", marginBottom: "0.5rem" }}>
              {depCurrency === "HTG"
                ? "Envoyez vos HTG via MonCash/NatCash à l'un des comptes admin ci-dessous, puis soumettez une preuve ici."
                : `Envoyez vos ${depCurrency} à l'une des adresses admin ci-dessous. Frais de dépôt ${(config.deposit_fee_rate ? config.deposit_fee_rate * 100 : 1).toFixed(1)}% prélevés à la validation (payés par le vendeur).`}
            </div>

            {/* Admin HTG payment numbers (always shown) */}
            <div style={{
              background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: "8px",
              padding: "0.5rem", marginBottom: "0.5rem"
            }} data-testid="crypto-admin-htg-numbers">
              <div style={{ fontSize: "0.7rem", color: "#0C4A6E", fontWeight: 700, marginBottom: "0.3rem" }}>
                Comptes admin MonCash / NatCash (HTG) :
              </div>
              <div style={{ display: "grid", gap: "0.22rem", marginBottom: "0.4rem" }}>
                {ADMIN_HTG_NUMBERS.map((num) => (
                  <div key={num} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    background: "white", borderRadius: "6px", padding: "0.25rem 0.5rem", border: "1px solid #BAE6FD"
                  }}>
                    <code style={{ fontSize: "0.76rem", color: "#0F172A", fontWeight: 600 }}>{num}</code>
                    <button type="button"
                      onClick={() => { navigator.clipboard.writeText(num); toast.success("Copié!"); }}
                      style={{ background: "#0EA5E9", color: "white", border: "none", borderRadius: "5px", padding: "0.12rem 0.4rem", fontSize: "0.64rem", cursor: "pointer" }}
                    >
                      Copier
                    </button>
                  </div>
                ))}
              </div>
              <a
                href={ADMIN_WA_URL_CRYPTO}
                target="_blank"
                rel="noreferrer"
                data-testid="crypto-admin-whatsapp"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "0.35rem",
                  background: "#25D366", color: "white", padding: "0.45rem",
                  borderRadius: "7px", textDecoration: "none", fontWeight: 700, fontSize: "0.75rem"
                }}
              >
                <Send size={13} /> Contacter le service (Groupe WhatsApp)
              </a>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.4rem", marginBottom: "0.4rem" }}>
              <select value={depCurrency} onChange={e => setDepCurrency(e.target.value)} className="form-input" data-testid="crypto-dep-currency">
                <option value="HTG">HTG (MonCash/NatCash)</option>
                {config.supported_symbols.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <input type="number" step="any" placeholder="Montant" value={depAmount} onChange={e => setDepAmount(e.target.value)} className="form-input" data-testid="crypto-dep-amount" />
            </div>
            {/* Admin addresses for selected crypto */}
            {depCurrency !== "HTG" && (() => {
              const relevant = adminAddresses.filter(a => a.crypto_symbol === depCurrency && a.is_active);
              if (relevant.length === 0) {
                return (
                  <div style={{ padding: "0.5rem", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: "8px", fontSize: "0.72rem", color: "#92400E", marginBottom: "0.4rem" }} data-testid="crypto-no-address">
                    Aucune adresse admin disponible pour <b>{depCurrency}</b>. Contactez l'admin ({config.admin_whatsapp}).
                  </div>
                );
              }
              return (
                <div style={{ marginBottom: "0.4rem" }} data-testid="crypto-admin-addresses">
                  <div style={{ fontSize: "0.7rem", color: "#475569", fontWeight: 700, marginBottom: "0.25rem" }}>
                    Adresse(s) admin {depCurrency} — envoyez vos fonds à:
                  </div>
                  {relevant.map(a => (
                    <div key={a.id} style={{
                      padding: "0.45rem 0.6rem", background: "#F0F9FF", border: "1px solid #BAE6FD",
                      borderRadius: "8px", fontSize: "0.72rem", marginBottom: "0.25rem"
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.3rem" }}>
                        <div style={{ fontWeight: 700, color: "#0C4A6E" }}>
                          {a.label || `${a.crypto_symbol}${a.network ? " · " + a.network : ""}`}
                        </div>
                        <button
                          type="button"
                          onClick={() => { navigator.clipboard.writeText(a.address); toast.success("Adresse copiée!"); }}
                          style={{ background: "#0EA5E9", color: "white", border: "none", borderRadius: "6px", padding: "0.1rem 0.4rem", fontSize: "0.65rem", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.2rem" }}
                          data-testid={`copy-addr-${a.id}`}
                        >
                          <Copy size={10} /> Copier
                        </button>
                      </div>
                      <code style={{ display: "block", fontSize: "0.68rem", color: "#0F172A", wordBreak: "break-all", marginTop: "0.15rem" }}>
                        {a.address}
                      </code>
                      {a.notes && <div style={{ fontSize: "0.65rem", color: "#64748B", marginTop: "0.1rem" }}>{a.notes}</div>}
                    </div>
                  ))}
                </div>
              );
            })()}
            {depCurrency !== "HTG" && (
              <input type="text" placeholder="Tx Hash (optionnel)" value={depTxHash} onChange={e => setDepTxHash(e.target.value)} className="form-input" style={{ marginBottom: "0.4rem" }} data-testid="crypto-dep-tx" />
            )}
            {depCurrency !== "HTG" && depAmount && Number(depAmount) > 0 && (
              <div style={{ padding: "0.4rem", background: "#F0FDF4", borderRadius: "8px", fontSize: "0.7rem", marginBottom: "0.4rem" }}>
                Frais dépôt ({((config.deposit_fee_rate || 0.01) * 100).toFixed(1)}%): <b>{(Number(depAmount) * (config.deposit_fee_rate || 0.01)).toFixed(6)} {depCurrency}</b> · Crédité: <b>{(Number(depAmount) * (1 - (config.deposit_fee_rate || 0.01))).toFixed(6)} {depCurrency}</b>
              </div>
            )}
            <label style={{ display: "block", padding: "0.6rem", border: "2px dashed #CBD5E1", borderRadius: "8px", textAlign: "center", cursor: "pointer", fontSize: "0.75rem", color: "#475569" }}>
              <Upload size={14} style={{ verticalAlign: "middle" }} /> {depProofUrl ? "Preuve téléversée ✓" : (depUploading ? "Envoi..." : "Téléverser une preuve (screenshot)")}
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleDepositProofUpload} data-testid="crypto-dep-proof" />
            </label>
            <button className="btn btn-primary" style={{ width: "100%", marginTop: "0.5rem" }} onClick={submitDeposit} data-testid="crypto-dep-submit">
              Soumettre le dépôt
            </button>

            {/* Reviews prompt — insist clients leave a review */}
            <div style={{
              marginTop: "0.7rem", padding: "0.55rem 0.65rem",
              background: "linear-gradient(135deg, #FEF3C7, #FDE68A)",
              border: "1px solid #F59E0B", borderRadius: "8px",
              display: "flex", alignItems: "center", gap: "0.4rem"
            }} data-testid="crypto-reviews-prompt">
              <Star size={18} color="#D97706" fill="#F59E0B" />
              <div style={{ fontSize: "0.74rem", color: "#78350F", flex: 1 }}>
                <b>Votre avis compte !</b> Après votre transaction, merci de laisser un avis pour nous aider à améliorer le service.
              </div>
              <a
                href={SERVICE_GROUP_URL}
                target="_blank"
                rel="noreferrer"
                style={{
                  background: "#D97706", color: "white", padding: "0.35rem 0.55rem",
                  borderRadius: "6px", textDecoration: "none", fontSize: "0.7rem",
                  display: "inline-flex", alignItems: "center", gap: "0.25rem", fontWeight: 700, whiteSpace: "nowrap"
                }}
                data-testid="crypto-leave-review"
              >
                <MessageCircle size={12} /> Laisser un avis
              </a>
            </div>
          </div>

          {/* Withdraw */}
          <div className="card">
            <div style={{ fontWeight: 800, fontSize: "0.88rem", marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <ArrowUpCircle size={15} color="#EF4444" /> Retirer
            </div>
            <div style={{ fontSize: "0.7rem", color: "#64748B", marginBottom: "0.5rem" }}>
              Votre solde sera verrouillé jusqu'à ce que l'admin effectue l'envoi.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.4rem", marginBottom: "0.4rem" }}>
              <select value={wdrCurrency} onChange={e => setWdrCurrency(e.target.value)} className="form-input" data-testid="crypto-wdr-currency">
                <option value="HTG">HTG (MonCash)</option>
                {config.supported_symbols.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <input type="number" step="any" placeholder="Montant" value={wdrAmount} onChange={e => setWdrAmount(e.target.value)} className="form-input" data-testid="crypto-wdr-amount" />
            </div>
            <input type="text" placeholder={wdrCurrency === "HTG" ? "Numéro MonCash (+509...)" : "Adresse du portefeuille"} value={wdrDestination} onChange={e => setWdrDestination(e.target.value)} className="form-input" data-testid="crypto-wdr-destination" />
            <button className="btn btn-danger" style={{ width: "100%", marginTop: "0.5rem", background: "#EF4444", color: "white" }} onClick={submitWithdraw} data-testid="crypto-wdr-submit">
              Demander le retrait
            </button>
          </div>
        </div>
      )}

      {/* MARKET TAB */}
      {tab === "market" && (
        <div>
          <div style={{ fontSize: "0.72rem", color: "#64748B", marginBottom: "0.6rem" }}>
            {ads.length} annonces disponibles · Cliquez sur "Acheter" pour initier un trade
          </div>
          {ads.length === 0 ? (
            <div style={{ padding: "1rem", background: "#F8FAFC", borderRadius: "8px", textAlign: "center", fontSize: "0.8rem", color: "#64748B" }}>
              Aucune annonce active pour le moment.
            </div>
          ) : ads.map(a => (
            <div key={a.id} className="card" style={{ marginBottom: "0.5rem" }} data-testid={`crypto-ad-${a.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: "0.92rem", color: "#0F172A" }}>
                    {a.crypto_symbol} · {fmtCrypto(a.amount_available)} disponible
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#475569", marginTop: "0.2rem" }}>
                    Prix: <b>{fmt(a.price_per_unit_htg)} HTG</b> / {a.crypto_symbol}
                  </div>
                  <div style={{ fontSize: "0.7rem", color: "#64748B" }}>
                    Vendeur: {a.seller_name}
                  </div>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => { setBuyAd(a); setBuyAmount(""); setBuyAddress(""); }} data-testid={`crypto-buy-${a.id}`}>
                  <ShoppingCart size={13} /> Acheter
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* BUY MODAL */}
      {buyAd && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "1rem" }} onClick={() => setBuyAd(null)}>
          <div style={{ background: "white", borderRadius: "14px", padding: "1rem", maxWidth: "420px", width: "100%" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 800, fontSize: "1rem", marginBottom: "0.5rem" }}>Acheter {buyAd.crypto_symbol}</div>
            <div style={{ fontSize: "0.75rem", color: "#64748B", marginBottom: "0.6rem" }}>
              Vendeur: <b>{buyAd.seller_name}</b><br/>
              Disponible: <b>{fmtCrypto(buyAd.amount_available)} {buyAd.crypto_symbol}</b><br/>
              Prix unitaire: <b>{fmt(buyAd.price_per_unit_htg)} HTG</b>
            </div>
            <input type="number" step="any" placeholder={`Quantité en ${buyAd.crypto_symbol}`} value={buyAmount} onChange={e => setBuyAmount(e.target.value)} className="form-input" data-testid="crypto-buy-amount" style={{ marginBottom: "0.4rem" }} />
            <input type="text" placeholder="Votre adresse crypto (optionnel)" value={buyAddress} onChange={e => setBuyAddress(e.target.value)} className="form-input" data-testid="crypto-buy-address" style={{ marginBottom: "0.5rem" }} />
            {buyAmount && Number(buyAmount) > 0 && (
              <div style={{ padding: "0.5rem", background: "#F0F9FF", borderRadius: "8px", fontSize: "0.75rem", marginBottom: "0.5rem" }}>
                Total HTG: <b>{fmt(Number(buyAmount) * buyAd.price_per_unit_htg)} HTG</b><br/>
                Frais acheteur (0.5%): <b>{fmtCrypto(Number(buyAmount) * 0.005)} {buyAd.crypto_symbol}</b> déduits du crypto reçu<br/>
                Vous recevrez: <b>{fmtCrypto(Number(buyAmount) * 0.995)} {buyAd.crypto_symbol}</b>
              </div>
            )}
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={submitBuy} data-testid="crypto-buy-confirm">Confirmer</button>
              <button className="btn" style={{ flex: 1, background: "#E2E8F0" }} onClick={() => setBuyAd(null)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* MINE TAB */}
      {tab === "mine" && (
        <div>
          {/* New Ad Form */}
          <div className="card" style={{ marginBottom: "0.8rem" }}>
            <div style={{ fontWeight: 800, fontSize: "0.88rem", marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <Plus size={15} /> Créer une annonce de vente
            </div>
            <div style={{ fontSize: "0.7rem", color: "#64748B", marginBottom: "0.5rem" }}>
              La quantité de crypto sera verrouillée jusqu'à la vente ou annulation.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.4rem", marginBottom: "0.4rem" }}>
              <select value={newAdSymbol} onChange={e => setNewAdSymbol(e.target.value)} className="form-input" data-testid="crypto-newad-symbol">
                {config.supported_symbols.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <input type="number" step="any" placeholder="Quantité" value={newAdAmount} onChange={e => setNewAdAmount(e.target.value)} className="form-input" data-testid="crypto-newad-amount" />
            </div>
            <input type="number" step="any" placeholder="Prix unitaire en HTG" value={newAdPrice} onChange={e => setNewAdPrice(e.target.value)} className="form-input" data-testid="crypto-newad-price" />
            {newAdAmount && newAdPrice && Number(newAdAmount) > 0 && Number(newAdPrice) > 0 && (
              <div style={{ padding: "0.4rem", background: "#F0FDF4", borderRadius: "8px", fontSize: "0.72rem", marginTop: "0.3rem" }}>
                Total attendu: <b>{fmt(Number(newAdAmount) * Number(newAdPrice))} HTG</b> (frais 0.5% = {fmt(Number(newAdAmount) * Number(newAdPrice) * 0.005)} HTG)
              </div>
            )}
            <button className="btn btn-primary" style={{ width: "100%", marginTop: "0.5rem" }} onClick={submitNewAd} data-testid="crypto-newad-submit">
              Publier l'annonce
            </button>
          </div>

          {/* My Ads List */}
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#475569", marginBottom: "0.4rem" }}>
            MES ANNONCES ({myAds.length})
          </div>
          {myAds.length === 0 ? (
            <div style={{ padding: "0.8rem", background: "#F8FAFC", borderRadius: "8px", textAlign: "center", fontSize: "0.78rem", color: "#64748B" }}>
              Vous n'avez pas encore d'annonce.
            </div>
          ) : myAds.map(a => (
            <div key={a.id} className="card" style={{ marginBottom: "0.4rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>{a.crypto_symbol} · {fmtCrypto(a.amount_available)}/{fmtCrypto(a.amount_total)}</div>
                  <div style={{ fontSize: "0.7rem", color: "#64748B" }}>
                    {fmt(a.price_per_unit_htg)} HTG/unité · <span style={{ color: statusColor(a.status) }}>{statusLabel(a.status)}</span>
                  </div>
                </div>
                {a.status === "active" && (
                  <button className="btn btn-sm" style={{ background: "#FEE2E2", color: "#B91C1C" }} onClick={() => cancelMyAd(a.id)} data-testid={`crypto-cancel-ad-${a.id}`}>
                    <Trash2 size={12} /> Annuler
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* HISTORY TAB */}
      {tab === "history" && (
        <div>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#475569", marginBottom: "0.4rem" }}>DÉPÔTS</div>
          {deposits.length === 0 ? <div style={{ fontSize: "0.75rem", color: "#94A3B8", marginBottom: "0.5rem" }}>Aucun dépôt.</div> : deposits.map(d => (
            <div key={d.id} style={{ padding: "0.5rem", background: "white", border: "1px solid #E2E8F0", borderRadius: "8px", marginBottom: "0.3rem", fontSize: "0.75rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span><b>{d.amount} {d.currency}</b></span>
                <span style={{ color: statusColor(d.status), fontWeight: 700 }}>{statusLabel(d.status)}</span>
              </div>
            </div>
          ))}
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#475569", margin: "0.6rem 0 0.4rem 0" }}>RETRAITS</div>
          {withdrawals.length === 0 ? <div style={{ fontSize: "0.75rem", color: "#94A3B8", marginBottom: "0.5rem" }}>Aucun retrait.</div> : withdrawals.map(d => (
            <div key={d.id} style={{ padding: "0.5rem", background: "white", border: "1px solid #E2E8F0", borderRadius: "8px", marginBottom: "0.3rem", fontSize: "0.75rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span><b>{d.amount} {d.currency}</b> → {short(d.destination, 10)}</span>
                <span style={{ color: statusColor(d.status), fontWeight: 700 }}>{statusLabel(d.status)}</span>
              </div>
            </div>
          ))}
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#475569", margin: "0.6rem 0 0.4rem 0" }}>TRADES</div>
          {trades.length === 0 ? <div style={{ fontSize: "0.75rem", color: "#94A3B8" }}>Aucun trade.</div> : trades.map(t => {
            const meIsBuyer = t.buyer_name && wallet;
            return (
              <div key={t.id} style={{ padding: "0.5rem", background: "white", border: "1px solid #E2E8F0", borderRadius: "8px", marginBottom: "0.3rem", fontSize: "0.75rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span><b>{fmtCrypto(t.amount_crypto)} {t.crypto_symbol}</b> · {fmt(t.total_htg)} HTG</span>
                  <span style={{ color: statusColor(t.status), fontWeight: 700 }}>{statusLabel(t.status)}</span>
                </div>
                <div style={{ fontSize: "0.68rem", color: "#64748B" }}>
                  {t.seller_name} → {t.buyer_name}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// =========================================================
// Component: Admin-facing Crypto Management
// =========================================================
export const CryptoExchangeAdmin = ({ apiCall }) => {
  const [tab, setTab] = useState("deposits");
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [proofView, setProofView] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [config, setConfig] = useState({ supported_symbols: [], deposit_fee_rate: 0.01 });
  const [newAddr, setNewAddr] = useState({ crypto_symbol: "USDT-TRC20", network: "TRC20", address: "", label: "", notes: "", is_active: true });
  const [editingAddr, setEditingAddr] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [d, w, t, s, a, cfg] = await Promise.all([
        apiCall("get", "/admin/crypto/deposits"),
        apiCall("get", "/admin/crypto/withdrawals"),
        apiCall("get", "/admin/crypto/trades"),
        apiCall("get", "/admin/crypto/stats"),
        apiCall("get", "/admin/crypto/addresses"),
        apiCall("get", "/crypto/config"),
      ]);
      setDeposits(d.data.deposits || []);
      setWithdrawals(w.data.withdrawals || []);
      setTrades(t.data.trades || []);
      setStats(s.data);
      setAddresses(a.data.addresses || []);
      setConfig(cfg.data);
    } catch (e) {
      toast.error("Erreur admin crypto");
    }
  }, [apiCall]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const validateDeposit = async (id) => {
    if (!window.confirm("Valider ce dépôt et créditer l'utilisateur?")) return;
    try {
      await apiCall("post", `/admin/crypto/deposit/${id}/validate`);
      toast.success("Dépôt validé");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const rejectDeposit = async (id) => {
    const reason = window.prompt("Raison du rejet?", "");
    if (reason === null) return;
    try {
      await apiCall("post", `/admin/crypto/deposit/${id}/reject`, { reason });
      toast.success("Dépôt rejeté");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const completeWithdrawal = async (id) => {
    const proof = window.prompt("URL preuve de paiement (optionnel):", "");
    if (!window.confirm("Marquer ce retrait comme complété? (Assurez-vous d'avoir envoyé les fonds)")) return;
    try {
      await apiCall("post", `/admin/crypto/withdrawal/${id}/complete`, { admin_proof_url: proof || "" });
      toast.success("Retrait complété");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const rejectWithdrawal = async (id) => {
    const reason = window.prompt("Raison du rejet? (les fonds seront retournés)", "");
    if (reason === null) return;
    try {
      await apiCall("post", `/admin/crypto/withdrawal/${id}/reject`, { reason });
      toast.success("Retrait rejeté");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const validateTrade = async (id) => {
    if (!window.confirm("Valider ce trade? Les soldes internes seront transférés avec frais 1%.")) return;
    try {
      await apiCall("post", `/admin/crypto/trade/${id}/validate`);
      toast.success("Trade complété");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const cancelTrade = async (id) => {
    const reason = window.prompt("Raison d'annulation? (les fonds verrouillés seront retournés)", "");
    if (reason === null) return;
    try {
      await apiCall("post", `/admin/crypto/trade/${id}/cancel`, { reason });
      toast.success("Trade annulé");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const createAddress = async () => {
    if (!newAddr.crypto_symbol || !newAddr.address.trim()) {
      toast.error("Symbole et adresse requis"); return;
    }
    try {
      await apiCall("post", "/admin/crypto/addresses", newAddr);
      toast.success("Adresse ajoutée");
      setNewAddr({ crypto_symbol: "USDT-TRC20", network: "TRC20", address: "", label: "", notes: "", is_active: true });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const saveAddress = async () => {
    if (!editingAddr) return;
    try {
      await apiCall("patch", `/admin/crypto/addresses/${editingAddr.id}`, {
        crypto_symbol: editingAddr.crypto_symbol,
        network: editingAddr.network,
        address: editingAddr.address,
        label: editingAddr.label,
        notes: editingAddr.notes,
        is_active: editingAddr.is_active,
      });
      toast.success("Adresse mise à jour");
      setEditingAddr(null);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const deleteAddress = async (id) => {
    if (!window.confirm("Supprimer cette adresse?")) return;
    try {
      await apiCall("delete", `/admin/crypto/addresses/${id}`);
      toast.success("Adresse supprimée");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const toggleAddressActive = async (a) => {
    try {
      await apiCall("patch", `/admin/crypto/addresses/${a.id}`, { is_active: !a.is_active });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const pendingDeps = deposits.filter(d => d.status === "pending");
  const pendingWdrs = withdrawals.filter(w => w.status === "pending");
  const pendingTrds = trades.filter(t => t.status === "pending_admin");

  return (
    <div data-testid="crypto-exchange-admin">
      {/* Stats */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.5rem", marginBottom: "0.8rem" }}>
          <div style={{ padding: "0.6rem", borderRadius: "10px", background: "#FFFBEB", border: "1px solid #FCD34D" }}>
            <div style={{ fontSize: "0.65rem", color: "#92400E", fontWeight: 700 }}>Dépôts en attente</div>
            <div style={{ fontSize: "1.3rem", fontWeight: 800 }}>{stats.deposits_pending}</div>
          </div>
          <div style={{ padding: "0.6rem", borderRadius: "10px", background: "#FEE2E2", border: "1px solid #FCA5A5" }}>
            <div style={{ fontSize: "0.65rem", color: "#991B1B", fontWeight: 700 }}>Retraits en attente</div>
            <div style={{ fontSize: "1.3rem", fontWeight: 800 }}>{stats.withdrawals_pending}</div>
          </div>
          <div style={{ padding: "0.6rem", borderRadius: "10px", background: "#DBEAFE", border: "1px solid #93C5FD" }}>
            <div style={{ fontSize: "0.65rem", color: "#1E40AF", fontWeight: 700 }}>Trades à valider</div>
            <div style={{ fontSize: "1.3rem", fontWeight: 800 }}>{stats.trades_pending}</div>
          </div>
          <div style={{ padding: "0.6rem", borderRadius: "10px", background: "#DCFCE7", border: "1px solid #86EFAC" }}>
            <div style={{ fontSize: "0.65rem", color: "#14532D", fontWeight: 700 }}>Frais collectés (HTG)</div>
            <div style={{ fontSize: "1.3rem", fontWeight: 800 }}>{fmt(stats.total_fee_htg_collected)}</div>
          </div>
          <div style={{ padding: "0.6rem", borderRadius: "10px", background: "#F1F5F9", border: "1px solid #CBD5E1" }}>
            <div style={{ fontSize: "0.65rem", color: "#0F172A", fontWeight: 700 }}>Volume HTG</div>
            <div style={{ fontSize: "1.3rem", fontWeight: 800 }}>{fmt(stats.total_htg_volume)}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: "0.3rem", marginBottom: "0.8rem", flexWrap: "wrap" }}>
        {[
          { k: "deposits", label: `Dépôts (${pendingDeps.length})` },
          { k: "withdrawals", label: `Retraits (${pendingWdrs.length})` },
          { k: "trades", label: `Trades (${pendingTrds.length})` },
          { k: "addresses", label: `Adresses (${addresses.length})` },
          { k: "all", label: "Historique" },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`admin-crypto-tab-${t.k}`}
            style={{
              padding: "0.45rem 0.7rem",
              borderRadius: "8px",
              border: "1px solid " + (tab === t.k ? "#2563EB" : "#E2E8F0"),
              background: tab === t.k ? "#2563EB" : "white",
              color: tab === t.k ? "white" : "#1E293B",
              fontSize: "0.78rem",
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* DEPOSITS */}
      {tab === "deposits" && (
        <div>
          {pendingDeps.length === 0 ? (
            <div style={{ padding: "1rem", background: "#F8FAFC", borderRadius: "8px", textAlign: "center", color: "#64748B", fontSize: "0.8rem" }}>
              Aucun dépôt en attente.
            </div>
          ) : pendingDeps.map(d => (
            <div key={d.id} className="card" style={{ marginBottom: "0.4rem" }} data-testid={`admin-deposit-${d.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: "180px" }}>
                  <div style={{ fontWeight: 800 }}>{d.owner_name} ({d.owner_type})</div>
                  <div style={{ fontSize: "0.8rem" }}>{d.amount} <b>{d.currency}</b></div>
                  {d.tx_hash && <div style={{ fontSize: "0.7rem", color: "#64748B", fontFamily: "monospace" }}>{short(d.tx_hash, 12)}</div>}
                  {d.note && <div style={{ fontSize: "0.7rem", color: "#64748B" }}>Note: {d.note}</div>}
                </div>
                <div style={{ display: "flex", gap: "0.3rem" }}>
                  {d.proof_url && (
                    <button className="btn btn-sm" onClick={() => setProofView(d.proof_url)} style={{ background: "#E0E7FF", color: "#3730A3" }} data-testid={`admin-deposit-proof-${d.id}`}>
                      Voir preuve
                    </button>
                  )}
                  <button className="btn btn-sm btn-primary" onClick={() => validateDeposit(d.id)} data-testid={`admin-validate-deposit-${d.id}`}>
                    <CheckCircle size={13} /> Valider
                  </button>
                  <button className="btn btn-sm" onClick={() => rejectDeposit(d.id)} style={{ background: "#FEE2E2", color: "#B91C1C" }} data-testid={`admin-reject-deposit-${d.id}`}>
                    <XCircle size={13} /> Rejeter
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* WITHDRAWALS */}
      {tab === "withdrawals" && (
        <div>
          {pendingWdrs.length === 0 ? (
            <div style={{ padding: "1rem", background: "#F8FAFC", borderRadius: "8px", textAlign: "center", color: "#64748B", fontSize: "0.8rem" }}>
              Aucun retrait en attente.
            </div>
          ) : pendingWdrs.map(w => (
            <div key={w.id} className="card" style={{ marginBottom: "0.4rem" }} data-testid={`admin-withdrawal-${w.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: "180px" }}>
                  <div style={{ fontWeight: 800 }}>{w.owner_name} ({w.owner_type})</div>
                  <div style={{ fontSize: "0.8rem" }}>{w.amount} <b>{w.currency}</b> → <code style={{ fontSize: "0.72rem", background: "#F1F5F9", padding: "0.1rem 0.3rem", borderRadius: "4px" }}>{w.destination}</code></div>
                </div>
                <div style={{ display: "flex", gap: "0.3rem" }}>
                  <button className="btn btn-sm btn-primary" onClick={() => completeWithdrawal(w.id)} data-testid={`admin-complete-wdr-${w.id}`}>
                    <CheckCircle size={13} /> Complété
                  </button>
                  <button className="btn btn-sm" onClick={() => rejectWithdrawal(w.id)} style={{ background: "#FEE2E2", color: "#B91C1C" }} data-testid={`admin-reject-wdr-${w.id}`}>
                    <XCircle size={13} /> Rejeter
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TRADES */}
      {tab === "trades" && (
        <div>
          {pendingTrds.length === 0 ? (
            <div style={{ padding: "1rem", background: "#F8FAFC", borderRadius: "8px", textAlign: "center", color: "#64748B", fontSize: "0.8rem" }}>
              Aucun trade à valider.
            </div>
          ) : pendingTrds.map(t => (
            <div key={t.id} className="card" style={{ marginBottom: "0.4rem" }} data-testid={`admin-trade-${t.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <div style={{ fontWeight: 800 }}>{fmtCrypto(t.amount_crypto)} {t.crypto_symbol} · {fmt(t.total_htg)} HTG</div>
                  <div style={{ fontSize: "0.75rem", color: "#64748B" }}>
                    Vendeur: <b>{t.seller_name}</b> → Acheteur: <b>{t.buyer_name}</b>
                  </div>
                  <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>
                    Frais: {fmt(t.fee_seller_htg)} HTG + {fmtCrypto(t.fee_buyer_crypto)} {t.crypto_symbol}
                  </div>
                  {t.buyer_crypto_address && <div style={{ fontSize: "0.65rem", color: "#64748B", fontFamily: "monospace" }}>Addr acheteur: {short(t.buyer_crypto_address, 10)}</div>}
                </div>
                <div style={{ display: "flex", gap: "0.3rem" }}>
                  <button className="btn btn-sm btn-primary" onClick={() => validateTrade(t.id)} data-testid={`admin-validate-trade-${t.id}`}>
                    <CheckCircle size={13} /> Valider
                  </button>
                  <button className="btn btn-sm" onClick={() => cancelTrade(t.id)} style={{ background: "#FEE2E2", color: "#B91C1C" }} data-testid={`admin-cancel-trade-${t.id}`}>
                    <XCircle size={13} /> Annuler
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ADDRESSES */}
      {tab === "addresses" && (
        <div data-testid="admin-crypto-addresses-panel">
          <div style={{ fontSize: "0.72rem", color: "#64748B", marginBottom: "0.6rem", padding: "0.5rem", background: "#FEF3C7", borderRadius: "8px", border: "1px solid #FDE68A" }}>
            Ces adresses sont visibles par tous les vendeurs/acheteurs dans le formulaire de dépôt. Frais de dépôt crypto: <b>{((config.deposit_fee_rate || 0.01) * 100).toFixed(1)}%</b> (payés par le vendeur lors de la validation).
          </div>

          {/* Create form */}
          <div className="card" style={{ marginBottom: "0.7rem" }}>
            <div style={{ fontWeight: 800, fontSize: "0.88rem", marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <Plus size={15} /> Ajouter une adresse de réception
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.4rem", marginBottom: "0.4rem" }}>
              <select className="form-input" value={newAddr.crypto_symbol} onChange={e => setNewAddr({ ...newAddr, crypto_symbol: e.target.value })} data-testid="admin-addr-symbol">
                {(config.supported_symbols || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <input className="form-input" placeholder="Réseau (ex: TRC20)" value={newAddr.network} onChange={e => setNewAddr({ ...newAddr, network: e.target.value })} data-testid="admin-addr-network" />
            </div>
            <input className="form-input" style={{ marginBottom: "0.4rem" }} placeholder="Adresse du portefeuille" value={newAddr.address} onChange={e => setNewAddr({ ...newAddr, address: e.target.value })} data-testid="admin-addr-address" />
            <input className="form-input" style={{ marginBottom: "0.4rem" }} placeholder="Libellé (optionnel, ex: Trust Wallet Principal)" value={newAddr.label} onChange={e => setNewAddr({ ...newAddr, label: e.target.value })} data-testid="admin-addr-label" />
            <input className="form-input" style={{ marginBottom: "0.4rem" }} placeholder="Notes/Instructions (optionnel)" value={newAddr.notes} onChange={e => setNewAddr({ ...newAddr, notes: e.target.value })} data-testid="admin-addr-notes" />
            <button className="btn btn-primary" style={{ width: "100%" }} onClick={createAddress} data-testid="admin-addr-submit">
              Ajouter l'adresse
            </button>
          </div>

          {/* List */}
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#475569", marginBottom: "0.3rem" }}>
            ADRESSES ENREGISTRÉES ({addresses.length})
          </div>
          {addresses.length === 0 ? (
            <div style={{ padding: "0.8rem", background: "#F8FAFC", borderRadius: "8px", textAlign: "center", fontSize: "0.78rem", color: "#64748B" }}>
              Aucune adresse. Ajoutez-en une pour que les vendeurs puissent déposer.
            </div>
          ) : addresses.map(a => (
            <div key={a.id} className="card" style={{ marginBottom: "0.3rem", opacity: a.is_active ? 1 : 0.55 }} data-testid={`admin-addr-row-${a.id}`}>
              {editingAddr && editingAddr.id === a.id ? (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.3rem", marginBottom: "0.3rem" }}>
                    <input className="form-input" value={editingAddr.crypto_symbol} onChange={e => setEditingAddr({ ...editingAddr, crypto_symbol: e.target.value })} placeholder="Symbole" />
                    <input className="form-input" value={editingAddr.network} onChange={e => setEditingAddr({ ...editingAddr, network: e.target.value })} placeholder="Réseau" />
                  </div>
                  <input className="form-input" style={{ marginBottom: "0.3rem" }} value={editingAddr.address} onChange={e => setEditingAddr({ ...editingAddr, address: e.target.value })} placeholder="Adresse" />
                  <input className="form-input" style={{ marginBottom: "0.3rem" }} value={editingAddr.label} onChange={e => setEditingAddr({ ...editingAddr, label: e.target.value })} placeholder="Libellé" />
                  <input className="form-input" style={{ marginBottom: "0.3rem" }} value={editingAddr.notes || ""} onChange={e => setEditingAddr({ ...editingAddr, notes: e.target.value })} placeholder="Notes" />
                  <div style={{ display: "flex", gap: "0.3rem" }}>
                    <button className="btn btn-primary btn-sm" onClick={saveAddress} data-testid={`admin-addr-save-${a.id}`}>Enregistrer</button>
                    <button className="btn btn-sm" style={{ background: "#E2E8F0" }} onClick={() => setEditingAddr(null)}>Annuler</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: "200px" }}>
                    <div style={{ fontWeight: 800, fontSize: "0.88rem" }}>
                      {a.crypto_symbol}{a.network ? ` · ${a.network}` : ""}
                      {!a.is_active && <span style={{ marginLeft: "0.4rem", padding: "0.1rem 0.3rem", borderRadius: "4px", fontSize: "0.6rem", background: "#FEE2E2", color: "#B91C1C" }}>Inactif</span>}
                    </div>
                    {a.label && <div style={{ fontSize: "0.72rem", color: "#475569" }}>{a.label}</div>}
                    <code style={{ display: "block", fontSize: "0.7rem", color: "#0F172A", wordBreak: "break-all", marginTop: "0.15rem", padding: "0.2rem 0.35rem", background: "#F1F5F9", borderRadius: "4px" }}>{a.address}</code>
                    {a.notes && <div style={{ fontSize: "0.65rem", color: "#64748B", marginTop: "0.15rem" }}>{a.notes}</div>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                    <button className="btn btn-sm" style={{ background: a.is_active ? "#FEF3C7" : "#DCFCE7", color: a.is_active ? "#92400E" : "#166534" }} onClick={() => toggleAddressActive(a)} data-testid={`admin-addr-toggle-${a.id}`}>
                      {a.is_active ? "Désactiver" : "Activer"}
                    </button>
                    <button className="btn btn-sm" style={{ background: "#DBEAFE", color: "#1E40AF" }} onClick={() => setEditingAddr({ ...a })} data-testid={`admin-addr-edit-${a.id}`}>
                      Modifier
                    </button>
                    <button className="btn btn-sm" style={{ background: "#FEE2E2", color: "#B91C1C" }} onClick={() => deleteAddress(a.id)} data-testid={`admin-addr-delete-${a.id}`}>
                      <Trash2 size={11} /> Suppr.
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* HISTORIQUE */}
      {tab === "all" && (
        <div>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#475569", marginBottom: "0.3rem" }}>Tous dépôts ({deposits.length})</div>
          {deposits.slice(0, 20).map(d => (
            <div key={d.id} style={{ padding: "0.4rem", background: "white", border: "1px solid #E2E8F0", borderRadius: "6px", marginBottom: "0.2rem", fontSize: "0.72rem" }}>
              {d.owner_name} · {d.amount} {d.currency} · <span style={{ color: statusColor(d.status) }}>{statusLabel(d.status)}</span>
            </div>
          ))}
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#475569", margin: "0.5rem 0 0.3rem 0" }}>Tous retraits ({withdrawals.length})</div>
          {withdrawals.slice(0, 20).map(w => (
            <div key={w.id} style={{ padding: "0.4rem", background: "white", border: "1px solid #E2E8F0", borderRadius: "6px", marginBottom: "0.2rem", fontSize: "0.72rem" }}>
              {w.owner_name} · {w.amount} {w.currency} · <span style={{ color: statusColor(w.status) }}>{statusLabel(w.status)}</span>
            </div>
          ))}
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#475569", margin: "0.5rem 0 0.3rem 0" }}>Tous trades ({trades.length})</div>
          {trades.slice(0, 20).map(t => (
            <div key={t.id} style={{ padding: "0.4rem", background: "white", border: "1px solid #E2E8F0", borderRadius: "6px", marginBottom: "0.2rem", fontSize: "0.72rem" }}>
              {t.seller_name} → {t.buyer_name} · {fmtCrypto(t.amount_crypto)} {t.crypto_symbol} · {fmt(t.total_htg)} HTG · <span style={{ color: statusColor(t.status) }}>{statusLabel(t.status)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Proof Modal */}
      {proofView && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "1rem" }} onClick={() => setProofView(null)}>
          <img src={proofView} alt="preuve" style={{ maxWidth: "90%", maxHeight: "90%", borderRadius: "8px" }} onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default CryptoExchangeUser;
